import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FreshnessIndicator } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useCustomersLite, type CustomerLite } from '../hooks/useCustomerProvisioning';
import { useLocationsForCustomer } from '../hooks/useAuvikTenants';
import {
  useMapUnifiSite,
  useRelinkPropertyDirectory,
  useRemapUnifiSites,
  useUnifiSites,
  useDiscoveredUnifiSites,
  useRegisterDiscoveredUnifiSite,
  type DiscoveredUnifiSite,
  type UnifiSiteRow,
} from '../hooks/useUnifiSites';
import { parseUnifiCounts, unifiFreshness, unifiOnlineDevices } from '../lib/unifi';
import { invokeTriggerTask } from '../lib/triggerTask';
import { formatDateTime, timeAgo } from '../lib/formatters';

/**
 * Integration status — renders `integration_status_snapshot()` v3.
 *
 * Every state comes from rows that exist (heartbeats, sync tables, counts);
 * nothing here infers "healthy" from an absence of data. The UniFi section
 * also exposes the two mapping actions from migration 20260918000035 and a
 * manual mapping form for sites the property directory could not place.
 */

type IntegrationState =
  | 'live'
  | 'ok'
  | 'stale'
  | 'offline'
  | 'not_configured'
  | 'unmapped'
  | 'never_synced'
  | 'last_run_failed'
  | 'never_run'
  | 'unsigned'
  | 'never_used'
  | string;

interface Snapshot {
  auvik?: {
    devices: number;
    devices_latest_at: string | null;
    alerts_latest_at: string | null;
    open_alerts: number;
    mapped_tenants: number;
    unmapped_tenants: number;
    last_poll_at: string | null;
    credentials_present: boolean;
    state: IntegrationState;
  };
  unifi?: {
    sites: number;
    mapped_sites: number;
    access_points: number;
    wlans: number;
    clients: number;
    sites_latest_at: string | null;
    last_poll_at: string | null;
    last_poll_ok: boolean | null;
    last_error: string | null;
    uptime_sample_latest_at: string | null;
    state: IntegrationState;
  };
  health_scoring?: {
    latest_at: string | null;
    state: IntegrationState;
  };
  m365?: {
    estimates_sent: number;
    with_message_id: number;
    state: IntegrationState;
  };
  audit_log?: {
    latest_at: string | null;
    recent_unsigned: number;
    state: IntegrationState;
  };
  zoho?: {
    customers_synced: number;
    contacts_synced: number;
    last_pull_at: string | null;
    last_pull_ok: boolean | null;
    last_push_at: string | null;
    last_push_ok: boolean | null;
    state: IntegrationState;
  };
  generated_at?: string;
}

function useIntegrationStatus() {
  return useQuery({
    queryKey: ['integration-status'],
    queryFn: async (): Promise<Snapshot> => {
      const { data, error } = await supabase.rpc('integration_status_snapshot');
      if (error) throw error;
      return ((data ?? {}) as unknown as Snapshot) ?? {};
    },
    refetchInterval: 30_000,
    staleTime: 0,
  });
}

/**
 * PazNet Learn → Documents mirror (`zoho-learn-sync`, hourly). Read straight
 * from the rows the task leaves behind: its latest heartbeat and the count of
 * documents it owns. No heartbeat and no rows means the Learn token was never
 * configured — the task no-ops without ZOHO_LEARN_REFRESH_TOKEN.
 */
interface ZohoLearnStatus {
  articles: number;
  last_run_at: string | null;
  last_run_ok: boolean | null;
  last_run_details: Record<string, unknown>;
  state: IntegrationState;
}

function useZohoLearnStatus() {
  return useQuery({
    queryKey: ['integration-status', 'zoho-learn'],
    queryFn: async (): Promise<ZohoLearnStatus> => {
      const [heartbeat, docs] = await Promise.all([
        supabase
          .from('integration_heartbeats')
          .select('occurred_at, ok, details')
          .eq('integration', 'zoho_learn_sync')
          .order('occurred_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase.from('documents').select('id', { count: 'exact', head: true }).eq('source', 'zoho_learn'),
      ]);
      if (heartbeat.error) throw heartbeat.error;
      if (docs.error) throw docs.error;
      const hb = heartbeat.data;
      const articles = docs.count ?? 0;
      const ageMs = hb ? Date.now() - new Date(hb.occurred_at).getTime() : null;
      const state: IntegrationState =
        !hb && articles === 0 ? 'not_configured'
        : !hb ? 'never_run'
        : hb.ok === false ? 'last_run_failed'
        : ageMs !== null && ageMs < 3 * 60 * 60 * 1000 ? 'live'
        : ageMs !== null && ageMs < 24 * 60 * 60 * 1000 ? 'stale'
        : 'offline';
      const details = hb && hb.details && typeof hb.details === 'object' && !Array.isArray(hb.details) ? (hb.details as Record<string, unknown>) : {};
      return { articles, last_run_at: hb?.occurred_at ?? null, last_run_ok: hb?.ok ?? null, last_run_details: details, state };
    },
    refetchInterval: 30_000,
    staleTime: 0,
  });
}

function useRunZohoLearnSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => invokeTriggerTask('zoho-learn-sync', {}, { idempotencyKey: `zoho-learn-sync-${Math.floor(Date.now() / 60_000)}` }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integration-status', 'zoho-learn'] });
    },
  });
}

function countOf(details: Record<string, unknown>, key: string): number | null {
  const value = details[key];
  return typeof value === 'number' ? value : Array.isArray(value) ? value.length : null;
}

function listOf(details: Record<string, unknown>, key: string): string[] {
  const value = details[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

const STATE_PRESENTATION: Record<string, { tone: 'success' | 'warning' | 'error' | 'muted'; label: string }> = {
  live: { tone: 'success', label: 'Live' },
  ok: { tone: 'success', label: 'OK' },
  stale: { tone: 'warning', label: 'Stale' },
  offline: { tone: 'error', label: 'Offline' },
  not_configured: { tone: 'muted', label: 'Not configured' },
  unmapped: { tone: 'warning', label: 'Unmapped' },
  never_synced: { tone: 'muted', label: 'Never synced' },
  last_run_failed: { tone: 'error', label: 'Last run failed' },
  never_run: { tone: 'muted', label: 'Never run' },
  unsigned: { tone: 'warning', label: 'Unsigned rows' },
  never_used: { tone: 'muted', label: 'Never used' },
};

const TONE_CLASS = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  error: 'bg-error-soft text-error',
  muted: 'bg-surface-3 text-text-muted',
};

function StatePill({ state }: { state: IntegrationState | undefined }) {
  if (!state) return null;
  const p = STATE_PRESENTATION[state] ?? { tone: 'muted' as const, label: state.replace(/_/g, ' ') };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[p.tone]}`}>{p.label}</span>;
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-xs">
      <span className="text-text-muted">{label}</span>
      <span className="text-text text-right break-words">{value === null || value === undefined || value === '' ? '—' : value}</span>
    </div>
  );
}

function when(iso: string | null | undefined): string | null {
  return iso ? `${formatDateTime(iso)} (${timeAgo(iso)})` : null;
}

function Card({ title, subtitle, state, children }: { title: string; subtitle: string; state?: IntegrationState; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-label={title}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-text">{title}</h2>
          <p className="text-xs text-text-faint">{subtitle}</p>
        </div>
        <StatePill state={state} />
      </div>
      {children}
    </section>
  );
}

const BTN =
  'inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-3 rounded-md text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50';

export function IntegrationStatusPage() {
  const { data, isLoading, error, refetch, isFetching } = useIntegrationStatus();
  const remap = useRemapUnifiSites();
  const relink = useRelinkPropertyDirectory();
  const learn = useZohoLearnStatus();
  const runLearnSync = useRunZohoLearnSync();
  const [learnMessage, setLearnMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const runLearn = async () => {
    setLearnMessage(null);
    try {
      const { runId } = await runLearnSync.mutateAsync();
      setLearnMessage({ tone: 'success', text: `Sync queued${runId ? ` (run ${runId})` : ''}. Documents update within a minute; this card refreshes on its own.` });
    } catch (e) {
      setLearnMessage({ tone: 'error', text: `Sync could not be queued: ${e instanceof Error ? e.message : String(e)}` });
    }
  };
  const [remapMessage, setRemapMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [relinkMessage, setRelinkMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const runRemap = async () => {
    setRemapMessage(null);
    try {
      const hosts = await remap.mutateAsync();
      setRemapMessage({ tone: 'success', text: `Remap finished: ${hosts} UniFi host${hosts === 1 ? '' : 's'} matched a property in the directory. Manual mappings were left untouched.` });
    } catch (e) {
      setRemapMessage({ tone: 'error', text: `Remap failed: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const runRelink = async () => {
    setRelinkMessage(null);
    try {
      const linked = await relink.mutateAsync();
      setRelinkMessage({ tone: 'success', text: `Relink finished: ${linked} location${linked === 1 ? '' : 's'} received a property code.` });
    } catch (e) {
      setRelinkMessage({ tone: 'error', text: `Relink failed: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-text-muted" role="status" aria-live="polite">Loading integration status…</div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-notification" role="alert">
        Could not load integration status{error instanceof Error && error.message ? `: ${error.message}` : ''}. Refresh to try again.
      </div>
    );
  }

  const s = data ?? {};
  const unifiUnmapped = s.unifi ? s.unifi.sites - s.unifi.mapped_sites : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-text">Integrations</h1>
          <p className="text-sm text-text-muted mt-0.5">
            State of Auvik, UniFi Site Manager, Zoho CRM, Office 365, the audit log and the health-scoring job, read from their own tables.
            {s.generated_at ? ` Snapshot taken ${timeAgo(s.generated_at)}.` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className={`${BTN} self-start bg-surface-3 text-text-muted hover:bg-surface-strong`}
        >
          {isFetching ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Auvik */}
        <Card title="Auvik" subtitle="Device polling and alert ingest (auvik-poll-devices, auvik-alert-ingest)" state={s.auvik?.state}>
          {s.auvik && (
            <>
              {s.auvik.state === 'not_configured' ? (
                <div className="rounded-md border border-border bg-surface-2 p-3 text-xs text-text-muted space-y-1.5 mb-3">
                  <p className="font-medium text-text">Auvik is not connected.</p>
                  <p>
                    {s.auvik.credentials_present
                      ? 'The poller has credentials but no tenant is mapped and no device has been stored.'
                      : 'The last poll reported missing Auvik credentials.'}
                    {' '}To connect it:
                  </p>
                  <ol className="list-decimal pl-4 space-y-0.5">
                    <li>Set a valid Auvik API key for the Trigger.dev environment (the poller records <code className="font-mono">missing_auvik_credentials</code> until it can authenticate).</li>
                    <li>
                      Sync the tenant inventory and map each tenant to a customer on{' '}
                      <Link to="/integrations/auvik" className="text-primary hover:underline">Auvik Tenants</Link>.
                    </li>
                  </ol>
                  <p>Until then, device, interface and alert counts stay empty and the portal shows “Auvik not connected”.</p>
                </div>
              ) : s.auvik.state === 'unmapped' ? (
                <p className="text-xs text-text-muted mb-3">
                  Auvik delivered {s.auvik.unmapped_tenants} tenant{s.auvik.unmapped_tenants === 1 ? '' : 's'} but none is mapped to a customer yet.{' '}
                  <Link to="/integrations/auvik" className="text-primary hover:underline">Map tenants</Link> to start device polling.
                </p>
              ) : (
                <FreshnessIndicator
                  lastUpdatedAt={s.auvik.devices_latest_at}
                  label="Devices"
                  expectedRefreshMs={10 * 60 * 1000}
                  staleMs={30 * 60 * 1000}
                  className="mb-2"
                />
              )}
              <div className="divide-y divide-divider">
                <Row label="Credentials" value={s.auvik.credentials_present ? 'Present' : 'Missing (per last poll)'} />
                <Row label="Last poll" value={when(s.auvik.last_poll_at) ?? 'Never'} />
                <Row label="Devices stored" value={s.auvik.devices} />
                <Row label="Devices last updated" value={when(s.auvik.devices_latest_at) ?? 'Never'} />
                <Row label="Mapped / unmapped tenants" value={`${s.auvik.mapped_tenants} / ${s.auvik.unmapped_tenants}`} />
                <Row label="Open alerts" value={s.auvik.mapped_tenants === 0 && s.auvik.devices === 0 ? 'Not connected' : s.auvik.open_alerts} />
                <Row label="Last alert ingested" value={when(s.auvik.alerts_latest_at) ?? 'Never'} />
              </div>
              <div className="pt-3 flex flex-wrap gap-3 text-xs">
                <Link to="/integrations/auvik" className="text-primary hover:underline">Auvik Tenants</Link>
                <Link to="/command-center" className="text-primary hover:underline">Command Center</Link>
              </div>
            </>
          )}
        </Card>

        {/* UniFi */}
        <Card title="UniFi Site Manager" subtitle="Site counts every 5 minutes (unifi-poll-wlan); uptime samples derive from device counts" state={s.unifi?.state}>
          {s.unifi && (
            <>
              <FreshnessIndicator
                lastUpdatedAt={s.unifi.sites_latest_at}
                label="Sites"
                expectedRefreshMs={10 * 60 * 1000}
                staleMs={30 * 60 * 1000}
                className="mb-2"
              />
              {s.unifi.state === 'not_configured' && (
                <p className="text-xs text-text-muted mb-2">No UniFi site has been stored and the poller has never reported in. Set the UniFi Site Manager API key for the Trigger.dev environment.</p>
              )}
              {s.unifi.state === 'last_run_failed' && (
                <p role="alert" className="text-xs text-error mb-2 break-words">The last poll failed{s.unifi.last_error ? `: ${s.unifi.last_error}` : ''}.</p>
              )}
              <div className="divide-y divide-divider">
                <Row label="Sites" value={s.unifi.sites} />
                <Row label="Mapped / unmapped" value={`${s.unifi.mapped_sites} / ${unifiUnmapped ?? '—'}`} />
                <Row label="Access points" value={s.unifi.access_points} />
                <Row label="WLANs" value={s.unifi.wlans} />
                <Row label="Clients stored" value={s.unifi.clients} />
                <Row label="Last poll" value={s.unifi.last_poll_at ? `${when(s.unifi.last_poll_at)} · ${s.unifi.last_poll_ok === false ? 'failed' : 'ok'}` : 'No heartbeat recorded'} />
                <Row label="Last error" value={s.unifi.last_error ?? 'None'} />
                <Row label="Latest uptime sample" value={when(s.unifi.uptime_sample_latest_at) ?? 'None yet'} />
              </div>
              <div className="pt-3 flex flex-wrap gap-2">
                <button type="button" onClick={runRemap} disabled={remap.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
                  {remap.isPending ? 'Remapping…' : 'Remap UniFi sites'}
                </button>
                <button type="button" onClick={runRelink} disabled={relink.isPending} className={`${BTN} border border-border bg-surface text-text hover:bg-surface-strong`}>
                  {relink.isPending ? 'Relinking…' : 'Relink property directory'}
                </button>
              </div>
              <p className="text-[11px] text-text-faint mt-2">
                Remap assigns sites to locations through <Link to="/properties" className="text-primary hover:underline">the property directory</Link> (UniFi host id → property code). Relink gives locations a property code by matching their name against the directory.
              </p>
              {remapMessage && <p role={remapMessage.tone === 'error' ? 'alert' : 'status'} className={`text-xs mt-2 break-words ${remapMessage.tone === 'error' ? 'text-error' : 'text-success'}`}>{remapMessage.text}</p>}
              {relinkMessage && <p role={relinkMessage.tone === 'error' ? 'alert' : 'status'} className={`text-xs mt-1 break-words ${relinkMessage.tone === 'error' ? 'text-error' : 'text-success'}`}>{relinkMessage.text}</p>}
            </>
          )}
        </Card>

        {/* Zoho */}
        <Card title="Zoho CRM" subtitle="Master of customer identity and contacts (zoho-pull-accounts, zoho-pull-contacts, webhooks)" state={s.zoho?.state}>
          {s.zoho && (
            <>
              {s.zoho.state === 'not_configured' && (
                <p className="text-xs text-text-muted mb-2">No pull has ever run and no customer carries a Zoho account id. Connect Zoho on the Zoho Sync page and run the first pull.</p>
              )}
              {s.zoho.state === 'never_run' && (
                <p className="text-xs text-text-muted mb-2">Customers carry Zoho ids but no pull task has recorded a heartbeat yet.</p>
              )}
              {s.zoho.state === 'last_run_failed' && (
                <p role="alert" className="text-xs text-error mb-2">The most recent pull reported a failure. Details are on the Zoho Sync page.</p>
              )}
              <div className="divide-y divide-divider">
                <Row label="Customers with a Zoho id" value={s.zoho.customers_synced} />
                <Row label="Contacts synced" value={s.zoho.contacts_synced} />
                <Row label="Last pull" value={s.zoho.last_pull_at ? `${when(s.zoho.last_pull_at)} · ${s.zoho.last_pull_ok === false ? 'failed' : 'ok'}` : 'Never'} />
                <Row label="Last push" value={s.zoho.last_push_at ? `${when(s.zoho.last_push_at)} · ${s.zoho.last_push_ok === false ? 'failed' : 'ok'}` : 'Never'} />
              </div>
              <div className="pt-3 text-xs">
                <Link to="/zoho-sync" className="text-primary hover:underline">Open Zoho Sync</Link>
              </div>
            </>
          )}
        </Card>

        {/* PazNet Learn */}
        <Card
          title="PazNet Learn"
          subtitle="Property manuals mirrored into customer Documents (zoho-learn-sync, hourly). Published articles only; removed articles disappear on the next pass."
          state={learn.data?.state ?? (learn.isError ? 'offline' : undefined)}
        >
          {learn.isError && (
            <p role="alert" className="text-xs text-error mb-2">
              Could not read the Learn sync status{learn.error instanceof Error && learn.error.message ? `: ${learn.error.message}` : ''}.
            </p>
          )}
          {learn.data && (
            <>
              {learn.data.state === 'not_configured' && (
                <p className="text-xs text-text-muted mb-2">
                  The sync has never run and no document carries the Learn source. Set ZOHO_LEARN_REFRESH_TOKEN in Trigger.dev (see docs/deployment-readiness.md §4) and run it once.
                </p>
              )}
              {learn.data.state === 'last_run_failed' && (
                <p role="alert" className="text-xs text-error mb-2">
                  The most recent pass failed{typeof learn.data.last_run_details.error === 'string' ? `: ${learn.data.last_run_details.error}` : ''}. Existing rows were left untouched.
                </p>
              )}
              <div className="divide-y divide-divider">
                <Row label="Articles in Documents" value={learn.data.articles} />
                <Row label="Last pass" value={learn.data.last_run_at ? `${when(learn.data.last_run_at)} · ${learn.data.last_run_ok === false ? 'failed' : 'ok'}` : 'Never'} />
                {learn.data.last_run_ok && (
                  <>
                    <Row label="Manuals synced" value={countOf(learn.data.last_run_details, 'manuals_synced')} />
                    <Row label="Written / removed" value={`${countOf(learn.data.last_run_details, 'articles_written') ?? '—'} / ${countOf(learn.data.last_run_details, 'articles_deleted') ?? '—'}`} />
                    <Row label="Manuals with no property" value={listOf(learn.data.last_run_details, 'manuals_unmatched').join(', ') || 'None'} />
                    <Row label="Manuals not shared externally" value={listOf(learn.data.last_run_details, 'manuals_not_shared').join(', ') || 'None'} />
                    <Row label="Chapters outside the sections" value={listOf(learn.data.last_run_details, 'unmapped_chapters').join(', ') || 'None'} />
                  </>
                )}
              </div>
              <div className="pt-3 flex flex-wrap items-center gap-2">
                <button type="button" onClick={runLearn} disabled={runLearnSync.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
                  {runLearnSync.isPending ? 'Queuing…' : 'Sync now'}
                </button>
                <a href="https://learn.paznetworks.com/team/c-hospitality-stanford-hotels-infrastructure" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                  Open the space in Learn
                </a>
              </div>
              {learnMessage && <p role={learnMessage.tone === 'error' ? 'alert' : 'status'} className={`text-xs mt-2 break-words ${learnMessage.tone === 'error' ? 'text-error' : 'text-success'}`}>{learnMessage.text}</p>}
            </>
          )}
        </Card>

        {/* M365 */}
        <Card title="Office 365 (Graph)" subtitle="Proposal send and reply tracking from sales@paznetworks.com" state={s.m365?.state}>
          {s.m365 && (
            <>
              {s.m365.state === 'not_configured' && (
                <p className="text-xs text-text-muted mb-2">
                  No proposal has been delivered through Graph yet ({s.m365.estimates_sent} estimate{s.m365.estimates_sent === 1 ? '' : 's'} marked sent, none with a Graph message id). Sending requires the Microsoft 365 application credentials and the Application Access Policy scoped to sales@paznetworks.com.
                </p>
              )}
              <div className="divide-y divide-divider">
                <Row label="Estimates marked sent" value={s.m365.estimates_sent} />
                <Row label="With Graph message id" value={s.m365.with_message_id} />
              </div>
            </>
          )}
        </Card>

        {/* Audit log */}
        <Card title="Audit log" subtitle="Append-only via RLS; HMAC row signing is reserved but not enabled" state={s.audit_log?.state}>
          {s.audit_log && (
            <>
              <FreshnessIndicator
                lastUpdatedAt={s.audit_log.latest_at}
                label="Audit"
                expectedRefreshMs={60 * 60 * 1000}
                staleMs={24 * 60 * 60 * 1000}
                className="mb-2"
              />
              <div className="divide-y divide-divider">
                <Row label="Latest entry" value={when(s.audit_log.latest_at) ?? 'None'} />
                <Row label="Unsigned rows (last 24h)" value={s.audit_log.recent_unsigned} />
              </div>
              <p className="text-xs text-text-muted mt-2">
                {s.audit_log.state === 'never_used'
                  ? 'Nothing has been written to the audit log yet.'
                  : s.audit_log.state === 'unsigned'
                    ? `${s.audit_log.recent_unsigned} row${s.audit_log.recent_unsigned === 1 ? '' : 's'} written in the last 24 hours ${s.audit_log.recent_unsigned === 1 ? 'has' : 'have'} no signature: the signing secret is not configured, so the log is protected by append-only row-level security only.`
                    : 'Every row written in the last 24 hours carries a signature.'}
              </p>
              <div className="pt-3 text-xs">
                <Link to="/audit" className="text-primary hover:underline">Open audit log</Link>
              </div>
            </>
          )}
        </Card>

        {/* Health scoring */}
        <Card title="Health scoring job" subtitle="Trigger.dev health-score-recompute (hourly) writes customer_health_scores" state={s.health_scoring?.state}>
          {s.health_scoring && (
            <>
              <FreshnessIndicator
                lastUpdatedAt={s.health_scoring.latest_at}
                label="Scores"
                expectedRefreshMs={60 * 60 * 1000}
                staleMs={2 * 60 * 60 * 1000}
                className="mb-2"
              />
              <div className="divide-y divide-divider">
                <Row label="Latest score written" value={when(s.health_scoring.latest_at) ?? 'Never'} />
              </div>
              <p className="text-xs text-text-muted mt-2">
                {s.health_scoring.state === 'never_run'
                  ? 'The job has never written a score. The customer roster computes health in the browser from live rows and labels it “Computed now”.'
                  : s.health_scoring.state === 'live'
                    ? 'Stored scores are fresh (under 2 hours) and are preferred by the roster.'
                    : 'Stored scores are older than 2 hours, so the roster recomputes health from live rows instead.'}
              </p>
            </>
          )}
        </Card>
      </div>

      <UnifiSitesSection />
    </div>
  );
}

/* ─── UniFi sites: unmapped table + manual mapping ─────────────────────── */

function UnifiSitesSection() {
  const { role } = useAuth();
  const canManage = role === 'admin' || role === 'superadmin';
  const { data: sites, isLoading, isError } = useUnifiSites();
  const discovery = useDiscoveredUnifiSites(canManage);
  const { data: customers } = useCustomersLite();
  const [showMapped, setShowMapped] = useState(false);
  const [registrationFeedback, setRegistrationFeedback] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const { unmapped, mapped } = useMemo(() => {
    const list = sites ?? [];
    return {
      unmapped: list.filter((s) => !s.customer_id),
      mapped: list.filter((s) => Boolean(s.customer_id)),
    };
  }, [sites]);

  return (
    <section className="rounded-lg border border-border bg-surface" aria-labelledby="unifi-sites-heading">
      <header className="px-4 sm:px-5 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 id="unifi-sites-heading" className="text-sm font-semibold text-text">Unmapped UniFi sites</h2>
          <p className="text-xs text-text-muted">
            Sites the property directory could not place. Map them to a customer and location here; manual mappings are never overridden by remap.
          </p>
        </div>
        <button type="button" onClick={() => setShowMapped((v) => !v)} aria-expanded={showMapped} className={`${BTN} self-start border border-border bg-surface text-text hover:bg-surface-strong`}>
          {showMapped ? 'Hide mapped sites' : `Show mapped sites (${mapped.length})`}
        </button>
      </header>

      {isLoading ? (
        <p className="p-5 text-sm text-text-muted">Loading UniFi sites…</p>
      ) : isError ? (
        <p role="alert" className="p-5 text-sm text-notification">Could not load UniFi sites. Refresh to try again.</p>
      ) : (sites?.length ?? 0) === 0 ? (
        <p className="p-6 text-center text-sm text-text-muted">No UniFi sites have been stored yet. The poller writes them once the Site Manager API key is configured.</p>
      ) : unmapped.length === 0 ? (
        <p className="p-6 text-center text-sm text-text-muted">All {sites!.length} UniFi site{sites!.length === 1 ? ' is' : 's are'} mapped to a customer.</p>
      ) : (
        <ul className="divide-y divide-border">
          {unmapped.map((site) => (
            <UnifiSiteRowItem key={site.id} site={site} customers={customers ?? []} />
          ))}
        </ul>
      )}

      {showMapped && mapped.length > 0 && (
        <div className="border-t border-border">
          <h3 className="px-4 sm:px-5 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Mapped sites</h3>
          <ul className="divide-y divide-border">
            {mapped.map((site) => (
              <UnifiSiteRowItem key={site.id} site={site} customers={customers ?? []} />
            ))}
          </ul>
        </div>
      )}
      {canManage && <section className="border-t border-border p-4 sm:p-5 space-y-3" aria-labelledby="discovered-unifi-title">
        <div className="flex flex-wrap justify-between gap-2"><div><h3 id="discovered-unifi-title" className="text-sm font-semibold text-text">Discovered monitoring UniFi providers</h3><p className="text-xs text-text-muted">Register an exact provider ID and host from the copied Portal catalog against an existing customer and location, including while monitoring is paused. Duplicate or missing host identities need review before assignment.</p></div><button type="button" className={`${BTN} border border-border text-text`} disabled={discovery.isFetching} onClick={() => void discovery.refetch()}>Refresh UniFi discovery</button></div>
        {discovery.isLoading && <p role="status" className="text-sm text-text-muted">Loading copied UniFi provider catalog…</p>}
        {discovery.isError && <p role="alert" className="text-sm text-error">The copied UniFi catalog is unavailable. Existing Portal mappings stay available above.</p>}
        {(discovery.data ?? []).filter((provider) => !(sites ?? []).some((site) => site.unifi_site_id === provider.id)).map((provider) => <DiscoveredUnifiRow key={JSON.stringify([provider.id, provider.hostId])} provider={provider} customers={customers ?? []} onResult={(result) => { setRegistrationFeedback(result); if (result.tone === 'success') setShowMapped(true); }} />)}
        {!discovery.isLoading && !discovery.isError && !(discovery.data ?? []).some((provider) => !(sites ?? []).some((site) => site.unifi_site_id === provider.id)) && <p className="text-xs text-text-muted">No additional source-only UniFi providers need Portal registration.</p>}
      </section>}
      {registrationFeedback && <p role={registrationFeedback.tone === 'error' ? 'alert' : 'status'} className={`px-4 pb-4 text-sm ${registrationFeedback.tone === 'error' ? 'text-error' : 'text-success'}`}>{registrationFeedback.text}</p>}
    </section>
  );
}

function DiscoveredUnifiRow({ provider, customers, onResult }: { provider: DiscoveredUnifiSite; customers: CustomerLite[]; onResult: (result: { tone: 'success' | 'error'; text: string }) => void }) {
  const [editing, setEditing] = useState(false);
  const site: UnifiSiteRow = { id: '', unifi_site_id: provider.id, unifi_host_id: provider.hostId,
    unifi_host_name: null, site_name: provider.name, customer_id: null, location_id: null,
    wifi_client_count: null, wired_client_count: null, ssid_count: null, mapped_by_user_id: null,
    mapped_at: null, attributes: null, updated_at: '', customer: null, location: null };
  return <div className="rounded border border-border p-3"><div className="flex flex-wrap justify-between gap-3"><div><p className="text-sm text-text">{provider.name}</p><p className="break-all text-xs text-text-muted">Provider {provider.id} · host {provider.hostId || 'not reported'}</p>{provider.ambiguous && <p className="text-xs text-warning">This provider has multiple hosts or no exact host. Resolve its source identity before assigning it.</p>}</div><button type="button" className={`${BTN} border border-border text-primary`} disabled={provider.ambiguous || editing} onClick={() => setEditing(true)}>Review UniFi provider assignment</button></div>{editing && <UnifiMappingForm site={site} customers={customers} discovered onClose={() => setEditing(false)} onResult={onResult} />}</div>;
}

function UnifiSiteRowItem({ site, customers }: { site: UnifiSiteRow; customers: CustomerLite[] }) {
  const { role } = useAuth();
  const canManage = role === 'admin' || role === 'superadmin';
  const [editing, setEditing] = useState(false);
  const counts = parseUnifiCounts(site.attributes);
  const online = unifiOnlineDevices(counts);
  const freshness = unifiFreshness(site.updated_at);
  const isMapped = Boolean(site.customer_id);

  return (
    <li className="px-4 sm:px-5 py-3">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-text truncate">{site.site_name ?? 'Unnamed site'}</p>
          <p className="text-xs text-text-muted break-words">
            Host {site.unifi_host_name ?? site.unifi_host_id ?? '—'}
            {counts ? ` · ${online ?? '—'} online / ${counts.offlineDevice ?? '—'} offline of ${counts.totalDevice ?? '—'} devices` : ' · no device counts reported'}
            {counts?.wifiClient !== null && counts?.wifiClient !== undefined ? ` · ${counts.wifiClient} Wi-Fi clients` : ''}
            {counts?.wiredClient !== null && counts?.wiredClient !== undefined ? ` · ${counts.wiredClient} wired` : ''}
          </p>
          <p className="break-all text-xs text-text-faint">Provider ID: {site.unifi_site_id}</p>
          <p className="text-xs text-text-faint">
            Last seen {timeAgo(site.updated_at)} · <span className={freshness === 'live' ? 'text-success' : freshness === 'stale' ? 'text-warning' : 'text-error'}>{freshness}</span>
            {isMapped && (
              <>
                {' · '}
                <Link to={`/customers/${site.customer_id}`} className="text-primary hover:underline">{site.customer?.name ?? 'Customer'}</Link>
                {site.location && (
                  <>
                    {' / '}
                    <Link to={`/locations/${site.location.id}`} className="text-primary hover:underline">{site.location.name}</Link>
                  </>
                )}
                {site.mapped_by_user_id ? ' · manual mapping' : ' · mapped by directory'}
              </>
            )}
          </p>
        </div>
        <div className="shrink-0">
          {canManage && !editing && (
            <button type="button" onClick={() => setEditing(true)} className={`${BTN} ${isMapped ? 'border border-border bg-surface text-text hover:bg-surface-strong' : 'bg-primary text-text-inverse hover:bg-primary-hover'}`}>
              {isMapped ? 'Re-map' : 'Map…'}
            </button>
          )}
        </div>
      </div>
      {canManage && editing && <UnifiMappingForm site={site} customers={customers} onClose={() => setEditing(false)} />}
    </li>
  );
}

function UnifiMappingForm({ site, customers, onClose, discovered = false, onResult }: { site: UnifiSiteRow; customers: CustomerLite[]; onClose: () => void; discovered?: boolean; onResult?: (result: { tone: 'success' | 'error'; text: string }) => void }) {
  const map = useMapUnifiSite();
  const register = useRegisterDiscoveredUnifiSite();
  const [expectedUpdatedAt] = useState(site.updated_at);
  const [customerId, setCustomerId] = useState(site.customer_id ?? '');
  const [locationId, setLocationId] = useState(site.location_id ?? '');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const { data: locations } = useLocationsForCustomer(customerId || null);

  const submit = async () => {
    if (!customerId || !locationId || reason.trim().length < 5) return;
    setMessage(null);
    try {
      if (discovered) await register.mutateAsync({ provider_id: site.unifi_site_id, customer_id: customerId, location_id: locationId, reason: reason.trim() });
      else await map.mutateAsync({ id: site.id, customer_id: customerId, location_id: locationId, expected_updated_at: expectedUpdatedAt, reason: reason.trim() });
      const result = { tone: 'success' as const, text: 'Provider ownership saved. If this changed a consolidated network, review and resume its monitoring registry before expecting collection.' };
      setMessage(result); onResult?.(result);
      window.setTimeout(onClose, 1500);
    } catch (e) {
      const result = { tone: 'error' as const, text: `Could not save this mapping: ${e instanceof Error ? e.message : String(e)}` };
      setMessage(result); onResult?.(result);
    }
  };

  const select = 'w-full min-h-[44px] md:min-h-[40px] rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50';

  return (
    <div className="mt-3 md:ml-4 md:pl-4 md:border-l-2 md:border-primary-soft space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Customer</span>
          <select
            aria-label="Customer"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setLocationId('');
            }}
            className={select}
          >
            <option value="">— Select customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.account_number})
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Location</span>
          <select
            aria-label="Location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={!customerId || (locations?.length ?? 0) === 0}
            className={select}
          >
            <option value="">{customerId ? ((locations?.length ?? 0) === 0 ? 'This customer has no locations' : '— Select exact location —') : 'Pick a customer first'}</option>
            {(locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.id})
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-xs text-text-muted break-all">Review provider {site.unifi_site_id} / host {site.unifi_host_id || 'unknown'} → customer {customerId || 'not selected'} / location {locationId || 'not selected'}.</p>
      <label className="block text-xs text-text-muted">Reason for provider ownership change<input className={`${select} mt-1`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></label>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button type="button" onClick={submit} disabled={!customerId || !locationId || reason.trim().length < 5 || map.isPending || register.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
          {map.isPending || register.isPending ? 'Saving…' : discovered ? 'Create reviewed UniFi ownership' : 'Save mapping'}
        </button>
        <button type="button" onClick={onClose} className={`${BTN} text-text-muted hover:text-text`}>Cancel</button>
      </div>
      <p className="text-[11px] text-text-faint">This saves the exact reviewed location with a revision check. Close and reopen the form after another administrator changes it. <Link to="/network-ops/mappings" className="text-primary underline">Review monitoring registry</Link>.</p>
      {message && <p role={message.tone === 'error' ? 'alert' : 'status'} className={`text-xs break-words ${message.tone === 'error' ? 'text-error' : 'text-success'}`}>{message.text}</p>}
    </div>
  );
}
